import React, { memo } from 'react';
import { Box, Paper } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { hasDiff } from '../delivery/deliveryCalc';
import type { ComparisonTotals, PharmacyRow } from '../delivery/deliveryCalc';
import { formatCell } from '../summary/summaryExport';

const BORDER = '1px solid #e2e8f0';

/** Стили заданы один раз на контейнере вложенными селекторами, а не на каждой ячейке */
const gridSx: SxProps<Theme> = {
  overflow: 'auto',
  maxHeight: 'calc(100vh - 250px)',
  minHeight: 160,
  '& table': { width: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', minWidth: 760 },

  '& th': {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    boxSizing: 'border-box',
    padding: '8px 12px',
    backgroundColor: '#f8fafc',
    color: '#475569',
    fontFamily: 'inherit',
    fontSize: '0.76rem',
    fontWeight: 700,
    textAlign: 'left',
    borderBottom: BORDER,
    whiteSpace: 'nowrap'
  },
  '& th.num': { textAlign: 'right' },

  '& tbody tr': { backgroundColor: '#ffffff' },
  '& tbody tr:hover': { backgroundColor: '#f1f5f9' },
  '& tbody tr.diff': { backgroundColor: '#fffbeb' },
  '& tbody tr.diff:hover': { backgroundColor: '#fef3c7' },

  '& td': {
    boxSizing: 'border-box',
    padding: '8px 12px',
    borderBottom: '1px solid #f1f5f9',
    fontFamily: 'inherit',
    fontSize: '0.86rem',
    color: '#475569',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  '& td.login': { fontWeight: 700, color: '#1e293b' },
  '& td.num': { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  '& td.nil': { color: '#cbd5e1' },
  '& td.bad': { fontWeight: 700, color: '#b45309' },
  '& td.ok': { color: '#15803d' },
  '& td.note': { color: '#b45309', fontWeight: 600 },

  // Итоги закреплены снизу
  '& tfoot td': {
    position: 'sticky',
    bottom: 0,
    zIndex: 2,
    backgroundColor: '#f1f5f9',
    color: '#0f172a',
    fontWeight: 700,
    borderTop: '1px solid #cbd5e1',
    borderBottom: 'none'
  },
  '& tfoot td.bad': { color: '#b45309' },
  '& tfoot td.ok': { color: '#15803d' }
};

/** Знаковая разница: «выписка минус доставка». Минус - настоящий знак, а не дефис, чтобы не путать с прочерком */
export function formatDiff(diff: number | null): string {
  if (diff === null) return '–';
  if (!hasDiff(diff)) return '0';
  return `${diff > 0 ? '+' : '−'}${formatCell(Math.abs(diff))}`;
}

function diffClass(diff: number | null): string {
  if (diff === null) return 'num nil';
  return hasDiff(diff) ? 'num bad' : 'num ok';
}

function amountClass(value: number | null): string {
  return value === null ? 'num nil' : 'num';
}

interface DeliveryGridProps {
  rows: PharmacyRow[];
  totals: ComparisonTotals;
}

const DeliveryGrid = memo(({ rows, totals }: DeliveryGridProps) => {
  const showUnassigned = totals.unassigned !== null && totals.unassigned !== 0;

  return (
    <Box sx={gridSx}>
      <table>
        <colgroup>
          <col style={{ width: 150 }} />
          <col style={{ width: 160 }} />
          <col />
          <col style={{ width: 150 }} />
          <col style={{ width: 150 }} />
          <col style={{ width: 150 }} />
        </colgroup>
        <thead>
          <tr>
            <th>Аптека</th>
            <th>Город</th>
            <th>Адрес</th>
            <th className="num">Выписка</th>
            <th className="num">Доставка аптеки</th>
            <th className="num">Разница</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const city = row.cities.join(', ');
            const address = row.addresses.join('; ');
            return (
              <tr key={row.login} className={hasDiff(row.diff) ? 'diff' : undefined}>
                <td className="login" title={row.login}>{row.login}</td>
                <td title={city}>{city}</td>
                <td title={address}>{address}</td>
                <td className={amountClass(row.statement)}>{formatCell(row.statement)}</td>
                <td className={amountClass(row.delivery)}>{formatCell(row.delivery)}</td>
                <td className={diffClass(row.diff)}>{formatDiff(row.diff)}</td>
              </tr>
            );
          })}
          {showUnassigned && (
            <tr className="diff">
              <td className="note" colSpan={3}>Заказы без аптеки</td>
              <td className="num">{formatCell(totals.unassigned)}</td>
              <td className="num nil">–</td>
              <td className="num nil">–</td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3}>Итого</td>
            <td className={amountClass(totals.statement)}>{formatCell(totals.statement)}</td>
            <td className={amountClass(totals.delivery)}>{formatCell(totals.delivery)}</td>
            <td className={diffClass(totals.diff)}>{formatDiff(totals.diff)}</td>
          </tr>
        </tfoot>
      </table>
    </Box>
  );
});

interface DeliveryTableProps extends DeliveryGridProps {
  /** Идет перезагрузка: показанные данные еще прежние */
  stale?: boolean;
}

const DeliveryTable: React.FC<DeliveryTableProps> = ({ rows, totals, stale }) => (
  <Paper
    elevation={0}
    sx={{ border: BORDER, borderRadius: '12px', overflow: 'hidden', transition: 'opacity 0.15s ease' }}
    style={{ opacity: stale ? 0.55 : 1 }}
  >
    <DeliveryGrid rows={rows} totals={totals} />
  </Paper>
);

export default memo(DeliveryTable);
